import { useState, useCallback, useRef } from 'react';
import { showConfirm } from '@/utils/common/alert';
import { UploadFile } from '@/components/common/admin/mobile/FileUploadSection';
import { 
  CreateMessageFormData,
  CreateMessageFieldChangeHandler,
  ProcessedRecipients,
  MessageError
} from '@/types/admin/messageFormTypes';
import { IMessageResident, ILeaseCustomer } from '@/types/admin/messages';

export interface CreateMessageFormErrors {
  subject?: string;
  messageContent?: string;
  recipients?: string;
  messageType?: string;
  files?: string;
}

const initialFormData: CreateMessageFormData = {
  subject: '',
  messageContent: '',
  recipients: [],
  messageType: 'groupChat',
  sendEmail: false,
  files: [],
  mismatchedGuarantors: [],
};

export const useCreateMessageForm = () => {
  const [formData, setFormData] = useState<CreateMessageFormData>(initialFormData);
  const [errors, setErrors] = useState<CreateMessageFormErrors>({});
  const initialFormDataRef = useRef<CreateMessageFormData>(initialFormData);

  // Handle field changes with type safety
  const handleFieldChange = useCallback((field: keyof CreateMessageFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when field is updated
    if (errors[field as keyof CreateMessageFormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }

    // Handle special logic for recipients and guarantors
    if (field === 'recipients' && Array.isArray(value)) {
      const recipients = value as IMessageResident[];
      
      // Check for mismatched guarantors when recipients change
      const guarantorRecipients = recipients.filter(
        (recipient) => recipient.leaseCustomers[0]?.customer_type === 'guarantor'
      );

      if (guarantorRecipients.length > 0 && recipients.length > 1) {
        const mismatchedGuarantors = guarantorRecipients.filter((guarantor) => {
          return Boolean(
            recipients.find(
              (recipient) =>
                recipient.leaseCustomers[0]?.lease_id !== guarantor.leaseCustomers[0]?.lease_id
            )
          );
        });

        if (mismatchedGuarantors.length > 0) {
          setFormData(prev => ({
            ...prev,
            recipients,
            mismatchedGuarantors,
          }));
          return;
        }
      }

      // Clear mismatched guarantors if no conflicts
      setFormData(prev => ({
        ...prev,
        recipients,
        mismatchedGuarantors: [],
      }));
    }
  }, [errors]);

  // Validate form
  const validateForm = useCallback((): CreateMessageFormErrors => {
    const newErrors: CreateMessageFormErrors = {};

    // Subject validation
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    } else if (formData.subject.length > 100) {
      newErrors.subject = 'Subject must be 100 characters or less';
    }

    // Message content validation
    if (!formData.messageContent.trim()) {
      newErrors.messageContent = 'Message content is required';
    } else if (formData.messageContent.length > 1000) {
      newErrors.messageContent = 'Message must be 1000 characters or less';
    }

    // Recipients validation
    if (formData.recipients.length === 0) {
      newErrors.recipients = 'At least one recipient is required';
    }

    // Files validation (optional but with limits)
    if (formData.files.length > 5) {
      newErrors.files = 'Maximum 5 files allowed';
    }

    setErrors(newErrors);
    return newErrors;
  }, [formData]);

  // Check if form has unsaved changes
  const hasUnsavedChanges = useCallback((): boolean => {
    const current = formData;
    const initial = initialFormDataRef.current;

    return (
      current.subject !== initial.subject ||
      current.messageContent !== initial.messageContent ||
      current.recipients.length !== initial.recipients.length ||
      current.messageType !== initial.messageType ||
      current.sendEmail !== initial.sendEmail ||
      current.files.length !== initial.files.length
    );
  }, [formData]);

  // Show discard alert
  const showDiscardAlert = useCallback(async (onConfirm: () => void) => {
    const confirmed = await showConfirm(
      'Discard Changes',
      'You have unsaved changes. Are you sure you want to discard them?',
      'Discard',
      'Cancel'
    );
    
    if (confirmed) {
      onConfirm();
    }
  }, []);

  // Reset form
  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setErrors({});
    initialFormDataRef.current = initialFormData;
  }, []);

  // Get form validation status
  const isFormValid = useCallback((): boolean => {
    const validationErrors = validateForm();
    return Object.keys(validationErrors).length === 0;
  }, [validateForm]);

  // Get recipient count by type with type safety
  const getRecipientStats = useCallback(() => {
    const assignedCount = formData.recipients.filter(
      (r: IMessageResident) => {
        const leaseCustomer = r.leaseCustomers[0];
        return leaseCustomer?.account && Object.keys(leaseCustomer.account || {}).length > 0;
      }
    ).length;

    const unassignedCount = formData.recipients.filter(
      (r: IMessageResident) => {
        const leaseCustomer = r.leaseCustomers[0];
        return !leaseCustomer?.account || Object.keys(leaseCustomer.account || {}).length <= 0;
      }
    ).length;

    const guarantorCount = formData.recipients.filter(
      (r: IMessageResident) => r.leaseCustomers[0]?.customer_type === 'guarantor'
    ).length;

    return {
      total: formData.recipients.length,
      assigned: assignedCount,
      unassigned: unassignedCount,
      guarantors: guarantorCount,
      mismatchedGuarantors: formData.mismatchedGuarantors?.length || 0,
    };
  }, [formData.recipients, formData.mismatchedGuarantors]);

  return {
    formData,
    errors,
    handleFieldChange,
    validateForm,
    hasUnsavedChanges,
    showDiscardAlert,
    resetForm,
    isFormValid,
    getRecipientStats,
  };
};
